import React from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { Shippings } from '@app/pages/checkout-app/types';
import { Checkbox } from '@app/react-common/components/checkbox/Checkbox';
import StatusText from '@app/react-common/components/status-text/StatusText';
import { ExpandableText } from '@app/react-common/components/expandable-text/ExpandableText';
import { ToggleButton } from '@app/react-common/components/toggle-button/ToggleButton';
import ThreeDotMenu, { ThreeDotMenuItem } from '@app/react-common/components/three-dot-menu/ThreeDotMenu';
import { LocationSettingsExpandableSettings } from '../location-settings-expandable-text/LocationSettingsExpandableSettings';
import { DisplayRatesComponent } from '../display-rates/DisplayRatesComponent';
import {
  getDeliveryMethodsDaysNTimes,
  getDisplayEstimatedDeliveryText,
  getEstimatedTimeText,
  isLiveRateMethod,
} from '../utils/DeliveryMethodUtils';
import { describeCartValueDiscountBasis, RuleNameToDisplayNameMap } from '@app/pages/checkout-app/configs';
import { convertCondition, convertValues } from '@app/pages/checkout-app/utils';
import { currencyUnitsToCurrencySymbolMap } from '@pdqdev/pdq-shared/dist/utils/currency-utils';
import { DragHandle } from '@app/react-common/components/drag-handle/DragHandle';

interface ShippingCondition {
  name: string;
  condition: string;
  values: string | number;
  optionalValue?: string;
  subsetGroupId?: string | number;
  weightUnit?: string;
}

export const formatCategoryDisplay = (category: string): string => {
  if (!category) return '';
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const displayCondition = (condition: ShippingCondition, currency: string) => {
  const nameValue = RuleNameToDisplayNameMap[condition?.name?.toLowerCase()] || '';
  const conditionValue = convertCondition(condition?.condition);
  const value = convertValues(condition, currency);
  const isCartValue = condition?.name === 'cartValue';
  const isMoneyValue = isCartValue || condition?.name === 'customerSpend';
  const discountBasis = isCartValue ? describeCartValueDiscountBasis(condition.optionalValue) : '';
  const weightUnit = condition?.weightUnit ? `- ${condition.weightUnit}` : '';

  return [nameValue, conditionValue, isMoneyValue ? currency : '', `${value}${discountBasis}`, weightUnit]
    .filter(Boolean)
    .join(' ');
};

export const getCurrencySign = (currencyUnit: string) => {
  return currencyUnitsToCurrencySymbolMap[currencyUnit] || '';
};

interface DeliveryMethodRowProps {
  deliveryMethod: Shippings;
  isSelected: boolean;
  onSelectChange: (checked: boolean) => void;
  onToggleEnabled: () => void;
  menuItems: ThreeDotMenuItem[];
  shopCurrency: string;
  className?: string;
  showCheckbox?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}

export const DeliveryMethodRow = ({
  deliveryMethod,
  isSelected,
  onSelectChange,
  onToggleEnabled,
  menuItems,
  shopCurrency,
  className = 'delivery-methods-list',
  showCheckbox = true,
  dragHandleProps,
}: DeliveryMethodRowProps) => {
  const conditionsGroup = (deliveryMethod.conditions as ShippingCondition[])
    .filter((x) => x.name !== 'locations')
    .reduce(
      (groups, item) => {
        (groups[item.subsetGroupId] = groups[item.subsetGroupId] || []).push(item);
        return groups;
      },
      {} as Record<string, ShippingCondition[]>,
    );
  const conditions = Object.values(conditionsGroup);

  const methodCurrency = deliveryMethod.methodCurrency ? getCurrencySign(deliveryMethod.methodCurrency) : shopCurrency;

  return (
    <div className={`${className}__main-content__dm-list__row`}>
      <div
        className={`${className}__table-column ${className}__table-column--checkbox ${className}__main-content__dm-list__col ${
          dragHandleProps ? `${className}__table-column--with-drag-handle` : ''
        }`}>
        {dragHandleProps && (
          <DragHandle
            dragHandleProps={dragHandleProps}
            accessibleLabel={`Reorder ${deliveryMethod.internalName || deliveryMethod.name}`}
          />
        )}
        {showCheckbox && <Checkbox checked={isSelected} onChange={onSelectChange} />}
      </div>
      <div
        className={`${className}__table-column ${className}__main-content__dm-list__col ${className}__table-column--identity`}>
        <p className={`${className}__identity-text`} title={deliveryMethod.internalName}>
          {deliveryMethod.internalName || '-'}
        </p>
        <div className={`${className}__tags`}>
          <StatusText label={formatCategoryDisplay(deliveryMethod.shippingMethod.category)} color={'primary'} />
          {deliveryMethod.isTestMode && <StatusText label={'Test mode'} color={'hyperlight20'} />}
          {deliveryMethod.runningTest && <StatusText label={'A/B test'} color={'hyperlight20'} />}
        </div>
        <p className={`${className}__identity-text`}>#{deliveryMethod.id}</p>
      </div>
      <div
        className={`${className}__table-column ${className}__main-content__dm-list__col ${className}__table-column--checkout-name`}>
        <h4>{deliveryMethod.name || '-'}</h4>
        <div>{deliveryMethod.notes}</div>
      </div>
      <div
        className={`${className}__table-column ${className}__main-content__dm-list__col ${className}__table-column--conditions`}>
        {conditions.length > 0 && (
          <ExpandableText
            title={'conditions'}
            rows={conditions.map((group) => {
              return group.map((x) => displayCondition(x, methodCurrency));
            })}
            isGroups={true}
          />
        )}
        {(deliveryMethod.displayEstimatedDeliveryBusinessDays || deliveryMethod.displayEstDelivery) && (
          <ExpandableText
            title={'delivery promise'}
            rows={[
              `${
                isLiveRateMethod(deliveryMethod.shippingMethod.category)
                  ? 'Processing time'
                  : 'Delivery promise settings'
              }`,
              ...getEstimatedTimeText(deliveryMethod.estimatedTimeConversions),
              ...getDisplayEstimatedDeliveryText(deliveryMethod),
            ]}
          />
        )}
        <LocationSettingsExpandableSettings shipping={deliveryMethod} />
        <ExpandableText title={'delivery time'} rows={getDeliveryMethodsDaysNTimes(deliveryMethod)} />
      </div>
      <div className={`${className}__table-column-small`}>
        <DisplayRatesComponent shipping={deliveryMethod} />
      </div>
      <div className={`${className}__main-content__actions`}>
        <ToggleButton checked={deliveryMethod.enabled} onChange={onToggleEnabled} />
        <ThreeDotMenu className={`${className}__main-content__actions__three-dot`} items={menuItems} />
      </div>
    </div>
  );
};
